/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { translations } from 'src/constants/translations';
import { ButtonApp } from 'src/components/Button';
import { IStatus, IComplex } from 'src/typings/complexes';
import { useGetAuthData } from 'src/hooks';
import { postPlacementStatus, patchPlacementStatus } from 'src/api';
import { ColorPicker } from 'src/components/profileComponents/ColorPicker';
import { PlacementShowModule } from 'src/typings/filter';
import { useGlobalGrid } from 'src/hooks/app/use-global-grid';
import { useStyles } from '../styles';

interface IStatusModal {
  isOpen: boolean;
  handleChangeOpen: (value: boolean) => void;
  refreshStatuses: () => void;
  status?: IStatus|null;
  complex: IComplex;
}

const schema = yup.object().shape({
  name: yup.string().required(translations.errorRequired),
  moduleName: yup.string().required(translations.errorRequired),
  color: yup.string().required(translations.errorRequired),
  crosstableColor: yup.string().required(translations.errorRequired),
});

export const StatusModal: React.FC<IStatusModal> = ({
  isOpen, handleChangeOpen, status, complex, refreshStatuses,
}) => {
  const { getGridsWidth } = useGlobalGrid();
  const styles = useStyles(getGridsWidth);
  const [color, setColor] = useState(status ? status.color : '#000');
  const [crossColor, setCrossColor] = useState(status && status.crosstableColor ? status.crosstableColor : '#000');
  const [error, setError] = useState('');
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();

  const {
    register, handleSubmit, formState,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const close = () => handleChangeOpen(false);

  const onSubmit = async (data:any) => {
    data.complex = complex['@id'];
    data.updateTimeInterval = data.temporaryType === 'fixed time'
      ? Math.floor(data.updateTimeInterval * 3600) : null;
    data.name = {
      ...status?.name,
      [complex.language]: data.name,
    };
    data.moduleName = {
      ...status?.moduleName,
      [complex.language]: data.moduleName,
    };

    data.showInModule = data.showInModule ? PlacementShowModule.show : PlacementShowModule.hide;

    data.crosstableName = {
      ...status?.crosstableName,
      [complex.language]: data.crosstableName,
    };

    let res;
    if (status) {
      res = await patchPlacementStatus(jwt, status.id, data);
    } else {
      res = await postPlacementStatus(jwt, data);
    }
    if (res.ok) {
      close();
      refreshStatuses();
    } else {
      setError(res.data['hydra:description']);
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={styles.modalWindow}
      open={isOpen}
      closeAfterTransition
    >
      <Fade in={isOpen}>
        <Paper
          style={{
            outline: 'none',
          }}
        >
          <Box width="910px" maxHeight="90vh" overflow="auto" py={10} px={11}>
            <Box mb={6} fontSize="24px" fontWeight={600} display="flex" alignItems="center" justifyContent="space-between">
              {status ? translations.editingStatus : translations.addingNewStatus}
              <CloseIcon onClick={close} className={styles.closeBtn} />
            </Box>
            <Box fontSize="18px" width="100%">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box display="flex">
                  <Box flex="1" mr={2}>
                    <Box mb={6} display="flex" flexDirection="column">
                      {translations.systemPlacement}
                      <Box width="260px" mt={2}>
                        <TextField
                          id="name"
                          variant="outlined"
                          name="name"
                          inputRef={register}
                          defaultValue={status && status.name && status.name[complex.language]}
                        />
                      </Box>
                    </Box>
                    <Box mb={6} display="flex" flexDirection="column" color="#ccc">
                      {translations.colorInSystemPlacement}
                      <Box position="relative" width="260px" mt={2}>
                        <Box
                          width="30px"
                          height="30px"
                          borderRadius="4px"
                          position="absolute"
                        />
                        <TextField
                          id="color"
                          variant="outlined"
                          disabled
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box flex="1" mr={2}>
                    <Box mb={6} display="flex" flexDirection="column">
                      {translations.crmAdminStatus}
                      <Box width="260px" mt={2}>
                        <TextField
                          id="crosstableName"
                          variant="outlined"
                          name="crosstableName"
                          inputRef={register}
                          defaultValue={status && status.crosstableName && status.crosstableName[complex.language]}
                        />
                      </Box>
                    </Box>
                    <Box mb={6} display="flex" flexDirection="column">
                      {translations.colorInCross}
                      <Box width="260px" mt={2}>
                        <TextField
                          id="crosstableColor"
                          name="crosstableColor"
                          hidden
                          value={crossColor}
                          inputRef={register}
                        />
                        <ColorPicker
                          color={crossColor}
                          colorSetter={(changeColor) => setCrossColor(changeColor)}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Box mb={6} display="flex" flexDirection="column">
                      {translations.websiteStatus}
                      <Box width="260px" mt={2}>
                        <TextField
                          id="moduleName"
                          variant="outlined"
                          name="moduleName"
                          inputRef={register}
                          defaultValue={status && (status.moduleName ? status.moduleName[complex.language] : status.moduleName[complex.language])}
                        />
                      </Box>
                    </Box>
                    <Box mb={6} display="flex" flexDirection="column">
                      {translations.colorInWebsite}
                      <Box position="relative" width="260px" mt={2}>
                        <TextField
                          id="color"
                          name="color"
                          hidden
                          value={color}
                          inputRef={register}
                        />
                        <ColorPicker
                          color={color}
                          colorSetter={(changeColor) => setColor(changeColor)}
                        />
                      </Box>
                    </Box>
                  </Box>

                </Box>
                <Box display="flex">
                  <Box mb={6}>
                    <Box>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            color="primary"
                            name="showInModule"
                            inputRef={register}
                            defaultChecked={status?.showInModule === PlacementShowModule.show}
                          />
                        )}
                        label={translations.showOnWebsite}
                      />
                    </Box>
                    <Box>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            color="primary"
                            name="amoCanBeSetForDeal"
                            inputRef={register}
                            defaultChecked={status?.amoCanBeSetForDeal}
                          />
                  )}
                        label={translations.allowAttachUnitInDeal}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="flex-end" gap="20px">
                  <ButtonApp
                    onClick={close}
                    text={translations.cancel}
                    color="primary"
                    variant="outlined"
                    type="button"
                    className={styles.btn}
                  />
                  <ButtonApp
                    text={translations.saveChanges}
                    color="primary"
                    variant="contained"
                    type="submit"
                    className={styles.btn}
                    disabled={!formState.isValid && !status}
                  />
                </Box>
              </form>
              <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert onClose={() => setError('')} elevation={6} variant="filled" severity="error">
                  {error}
                </Alert>
              </Snackbar>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};
